import { useEffect, useId, useMemo, useState } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/utils/cn';

import { DEFAULT_BRAND_PRIMARY } from '@/lib/brand-colors';

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

const normalizeHex = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return withHash.toUpperCase();
};

const resolvePickerHex = (value: string | null, fallback: string): string => {
  const normalized = normalizeHex(value ?? fallback);
  return HEX_COLOR.test(normalized) ? normalized : fallback;
};

export interface ColorPickerProps {
  value: string | null;
  onChange: (value: string) => void;
  /** Fallback when the value is empty or invalid. */
  placeholder?: string;
  /** Accessible name for the graphical picker control. */
  pickerLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  className?: string;
}

export const ColorPicker = ({
  value,
  onChange,
  placeholder = DEFAULT_BRAND_PRIMARY,
  pickerLabel,
  disabled = false,
  invalid = false,
  id,
  className,
  ...aria
}: ColorPickerProps) => {
  const fallbackId = useId();
  const fieldId = id ?? fallbackId;
  const hexInputId = `${fieldId}-hex`;
  const fallback = placeholder.toUpperCase();

  const [hexDraft, setHexDraft] = useState(value ?? '');

  useEffect(() => {
    setHexDraft(value ?? '');
  }, [value]);

  const pickerValue = useMemo(
    () => resolvePickerHex(value ?? hexDraft, fallback),
    [fallback, hexDraft, value],
  );

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      <input
        id={fieldId}
        type="color"
        value={pickerValue}
        disabled={disabled}
        aria-label={pickerLabel}
        aria-describedby={aria['aria-describedby']}
        aria-invalid={aria['aria-invalid'] ?? (invalid || undefined)}
        className={cn(
          'h-10 w-14 shrink-0 cursor-pointer rounded-md border bg-surface p-1 transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          invalid ? 'border-danger' : 'border-border hover:border-border-strong',
        )}
        onChange={(event) => {
          const next = event.target.value.toUpperCase();
          setHexDraft(next);
          onChange(next);
        }}
      />

      <Input
        id={hexInputId}
        value={hexDraft}
        disabled={disabled}
        invalid={invalid}
        placeholder={fallback}
        spellCheck={false}
        autoComplete="off"
        className="w-32 font-mono text-body-sm"
        onChange={(event) => {
          const next = normalizeHex(event.target.value);
          setHexDraft(next);
          if (HEX_COLOR.test(next)) onChange(next);
        }}
        onBlur={() => {
          const normalized = normalizeHex(hexDraft);
          if (HEX_COLOR.test(normalized)) {
            setHexDraft(normalized);
            onChange(normalized);
          }
        }}
      />
    </div>
  );
};
